'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getExperienceById, experiences } from '@/lib/data'
import BookNowCTA from '@/components/BookNowCTA'
import styles from '@/styles/experience-page-option3.module.css'
import { BreadcrumbSchema } from '@/lib/schema'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import '@/components/Adventures.css'

interface GalleryItem {
  src: string
  alt: string
  title: string
  description: string
  details: string
}

const gallerySrcs = [
  '/images/experiences/laparva-signature/gallery-breakfast-penthouse.webp',
  '/images/experiences/laparva-signature/gallery-family-skiing.webp',
  '/images/experiences/laparva-signature/gallery-bbq-evening.webp',
  '/images/experiences/laparva-signature/gallery-oysters-seafood.webp',
  '/images/experiences/laparva-signature/gallery-champagne-toast-andes.webp',
  '/images/experiences/laparva-signature/gallery-luxury-bedroom.webp',
  '/images/experiences/laparva-signature/signature-chairlift-group.webp',
]

export default function LaParvaSignaturePage() {
  const thisExperience = getExperienceById('laparva-signature')
  const t = useTranslations('experiences')
  const tCommon = useTranslations('common')
  const params = useParams()
  const locale = params.locale as string

  const relatedExperiences = experiences.filter(e => e.id !== 'laparva-signature').slice(0, 2)

  const galleryItems: GalleryItem[] = gallerySrcs.map((src, idx) => ({
    src,
    alt: t(`laparvaSignature.gallery.item${idx + 1}.alt`),
    title: t(`laparvaSignature.gallery.item${idx + 1}.title`),
    description: t(`laparvaSignature.gallery.item${idx + 1}.description`),
    details: t(`laparvaSignature.gallery.item${idx + 1}.details`),
  }))

  const [modalItem, setModalItem] = useState<GalleryItem | null>(null)

  const mainRef = useScrollAnimation<HTMLElement>({
    threshold: 0.12,
    rootMargin: '0px 0px -60px 0px',
  })

  const galleryCarouselRef = useRef<HTMLDivElement | null>(null)
  const [galleryRevealed, setGalleryRevealed] = useState(false)

  useEffect(() => {
    const node = galleryCarouselRef.current
    if (!node || galleryRevealed) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setGalleryRevealed(true)
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [galleryRevealed])

  const openModal = useCallback((item: GalleryItem) => {
    setModalItem(item)
    document.body.style.overflow = 'hidden'
  }, [])

  const closeModal = useCallback(() => {
    setModalItem(null)
    document.body.style.overflow = ''
  }, [])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    if (modalItem) {
      window.addEventListener('keydown', handleEsc)
    }
    return () => {
      window.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [modalItem, closeModal])

  if (!thisExperience) {
    return <div>{t('common.experienceNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/experiences/` },
        { name: t('laparvaSignature.title'), href: `/${locale}/experiences/laparva-signature/` },
      ]} />
      <Header />

      <HeroParallax
        backgroundImage={thisExperience.heroImage}
        title={t('laparvaSignature.title')}
        subtitle={t('laparvaSignature.subtitle')}
        location={t('laparvaSignature.location')}
      />

      <main className={styles.pageWrapper} ref={mainRef}>
        <section className={styles.experienceDetails}>
          <div className="container">
            {/* Editorial Hero Block */}
            <div className={styles.editorialHero}>
              <div className={styles.heroImageBlock} data-scroll-item>
                <img
                  src="/images/experiences/laparva/laparva-feature.webp"
                  alt={t('laparvaSignature.alts.heroMain')}
                  className={styles.heroMainImage}
                />
                <div className={styles.heroImageAccent} />
              </div>
              <div className={styles.heroTextBlock} data-scroll-item style={{ transitionDelay: '150ms' }}>
                <span className={styles.experienceLabel}>{t('laparvaSignature.label')}</span>
                <h2 className={styles.editorialTitle}>{t('laparvaSignature.editorialTitle')}</h2>
                <p className={styles.editorialTagline}>{t('laparvaSignature.editorialTagline')}</p>
                <div className={styles.heroNarrative}>
                  <p className={styles.heroNarrativeLead}>
                    {t('laparvaSignature.narrativeLead')}
                  </p>
                  <p className={styles.heroNarrativeText}>
                    {t('laparvaSignature.narrativeText')}
                  </p>
                </div>
              </div>
            </div>

            {/* What's Included -- Luxury Card with Integrated Image */}
            <div className={styles.luxuryIncludedSection}>
              <div className={styles.luxuryIncludedCard}>
                {/* Left: Content */}
                <div className={styles.luxuryIncludedContent} data-scroll-item>
                  <span className={styles.luxuryOverline}>{t('laparvaSignature.overline')}</span>
                  <h3 className={styles.luxuryIncludedTitle}>{t('common.whatsIncluded')}</h3>
                  <p className={styles.luxuryIncludedTagline}>
                    {t('laparvaSignature.includedTagline')}
                  </p>

                  <div className={styles.luxuryItemsList}>
                    <div className={styles.luxuryItem}>
                      <span className={styles.luxuryItemIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                          <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                      </span>
                      <span className={styles.luxuryItemText}>
                        {t('laparvaSignature.included.item1')}
                      </span>
                    </div>

                    <div className={styles.luxuryItem}>
                      <span className={styles.luxuryItemIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
                          <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
                          <line x1="6" y1="1" x2="6" y2="4" />
                          <line x1="10" y1="1" x2="10" y2="4" />
                          <line x1="14" y1="1" x2="14" y2="4" />
                        </svg>
                      </span>
                      <span className={styles.luxuryItemText}>
                        {t('laparvaSignature.included.item2')}
                      </span>
                    </div>

                    <div className={styles.luxuryItem}>
                      <span className={styles.luxuryItemIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2L2 7l10 5 10-5-10-5z" />
                          <path d="M2 17l10 5 10-5" />
                          <path d="M2 12l10 5 10-5" />
                        </svg>
                      </span>
                      <span className={styles.luxuryItemText}>
                        {t('laparvaSignature.included.item3')}
                      </span>
                    </div>

                    <div className={styles.luxuryItem}>
                      <span className={styles.luxuryItemIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7z" />
                          <circle cx="12" cy="9" r="2.5" />
                        </svg>
                      </span>
                      <span className={styles.luxuryItemText}>
                        {t('laparvaSignature.included.item4')}
                      </span>
                    </div>

                    <div className={styles.luxuryItem}>
                      <span className={styles.luxuryItemIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </span>
                      <span className={styles.luxuryItemText}>
                        {t('laparvaSignature.included.item5')}
                      </span>
                    </div>

                    <div className={styles.luxuryItem}>
                      <span className={styles.luxuryItemIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                          <line x1="9" y1="9" x2="9.01" y2="9" />
                          <line x1="15" y1="9" x2="15.01" y2="9" />
                        </svg>
                      </span>
                      <span className={styles.luxuryItemText}>
                        {t('laparvaSignature.included.item6')}
                      </span>
                    </div>

                    <div className={styles.luxuryItem}>
                      <span className={styles.luxuryItemIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                        </svg>
                      </span>
                      <span className={styles.luxuryItemText}>
                        {t('laparvaSignature.included.item7')} <em>{t('laparvaSignature.included.item7Optional')}</em>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: Lifestyle Image */}
                <div className={styles.luxuryIncludedImageWrapper} data-scroll-item style={{ transitionDelay: '180ms' }}>
                  <img
                    src="/images/experiences/laparva-signature/signature-toast-mountain-view.webp"
                    alt={t('laparvaSignature.alts.champagneToast')}
                    className={styles.luxuryIncludedImage}
                    loading="lazy"
                  />
                  <div className={styles.luxuryImageCaption}>
                    <span>{t('laparvaSignature.celebrateTogether')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Immersive Break — hidden on mobile, content moves to carousel */}
            <div className={`${styles.immersiveBreak} ${styles.hideOnMobile}`}>
              <div className={styles.immersiveImage} data-scroll-item>
                <img
                  src="/images/experiences/laparva-signature/signature-chairlift-group.webp"
                  alt={t('laparvaSignature.alts.chairlift')}
                  loading="lazy"
                />
              </div>
              <div className={styles.immersiveQuote} data-scroll-item style={{ transitionDelay: '160ms' }}>
                <blockquote>
                  {t('laparvaSignature.immersiveQuote')}
                  <span className={styles.quoteAccent}>{t('laparvaSignature.immersiveAccent')}</span>
                </blockquote>
              </div>
            </div>

          </div>
        </section>

        <div data-scroll-item>
          <BookNowCTA
            variant="inline"
            productId="laparva-signature"
            productType="experience"
            productName="La Parva Signature Experience"
            bookingMode={thisExperience.bookingMode || 'whatsapp'}
          />
        </div>

        <section className={styles.gallery}>
          <div className={styles.galleryContainer}>
            <div className={styles.sectionHeader} data-scroll-item>
              <h2 className={styles.sectionTitle}>{t('laparvaSignature.galleryTitle')}</h2>
              <p className={styles.sectionDescription}>
                {t('laparvaSignature.galleryDescription')}
              </p>
            </div>

            <div
              className={styles.galleryCarousel}
              ref={galleryCarouselRef}
              data-revealed={galleryRevealed ? 'true' : 'false'}
            >
              <Swiper
                modules={[Navigation, Pagination]}
                slidesPerView={1}
                spaceBetween={24}
                loop={true}
                grabCursor={true}
                navigation={{
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev',
                }}
                pagination={{
                  el: `.${styles.galleryPagination}`,
                  clickable: true,
                  dynamicBullets: true,
                }}
                breakpoints={{
                  640: { slidesPerView: 2, spaceBetween: 24 },
                  1024: { slidesPerView: 3, spaceBetween: 32 },
                }}
                className={styles.gallerySwiper}
              >
                {galleryItems.map((item, idx) => (
                  <SwiperSlide key={idx}>
                    <article
                      className={styles.galleryCard}
                      onClick={() => openModal(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') openModal(item) }}
                    >
                      <div className={styles.galleryCardImage}>
                        <img src={item.src} alt={item.alt} loading="lazy" />
                      </div>
                      <div className={styles.galleryCardOverlay}>
                        <h3 className={styles.galleryCardTitle}>{item.title}</h3>
                        <div className={styles.galleryCardContent}>
                          <p>{item.description}</p>
                          <span className={styles.galleryCardLink}>
                            {t('laparvaSignature.viewDetails')} &rarr;
                          </span>
                        </div>
                      </div>
                    </article>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className="swiper-button-prev"></div>
              <div className="swiper-button-next"></div>
              <div className={styles.galleryPagination}></div>
            </div>

            <div className={styles.sectionCta} data-scroll-item>
              <a href={`/${locale}/experiences/`} className="btn-primary">
                {t('common.exploreExperiences')} &rarr;
              </a>
            </div>
          </div>
        </section>

        {/* Gallery Detail Modal */}
        {modalItem && (
          <div
            className={styles.galleryModal}
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
            aria-label={modalItem.title}
          >
            <div
              className={styles.galleryModalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={styles.galleryModalClose}
                onClick={closeModal}
                aria-label={t('common.closeModal')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              <div className={styles.galleryModalImageWrap}>
                <img src={modalItem.src} alt={modalItem.alt} />
              </div>

              <div className={styles.galleryModalBody}>
                <span className={styles.galleryModalOverline}>{t('laparvaSignature.modalOverline')}</span>
                <h3 className={styles.galleryModalTitle}>{modalItem.title}</h3>
                <div className={styles.galleryModalDivider} />
                <p className={styles.galleryModalText}>{modalItem.details}</p>
              </div>
            </div>
          </div>
        )}

        <section className={styles.relatedSection}>
          <div className="container">
            <h2 data-scroll-item>{t('common.relatedExperiences')}</h2>
            <div className={styles.relatedGrid}>
              {relatedExperiences.map((experience, i) => (
                <article
                  key={experience.id}
                  className={styles.relatedCard}
                  data-scroll-item
                  style={{ transitionDelay: `${120 + i * 120}ms` }}
                >
                  <div className={styles.relatedImage}>
                    <img src={experience.heroImage} alt={experience.title} loading="lazy" />
                  </div>
                  <div className={styles.relatedOverlay}>
                    <h3 className={styles.relatedTitle}>{experience.title}</h3>
                    <div className={styles.relatedContent}>
                      <p>{experience.subtitle}</p>
                      <a href={`/${locale}/experiences/${experience.id}/`} className={styles.relatedLink}>
                        {t('common.explore')} &rarr;
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <div data-scroll-item>
          <BookNowCTA
            variant="closing"
            productId="laparva-signature"
            productType="experience"
            productName="La Parva Signature Experience"
            bookingMode={thisExperience.bookingMode || 'whatsapp'}
          />
        </div>
      </main>

      <Footer />
    </>
  )
}
